import React from 'react';
import { motion } from 'framer-motion';
import { Github, ExternalLink, Package, Terminal } from 'lucide-react';
import { publishedPackages } from '../../data/mock';

const PackageCard = ({ pkg, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.2 }}
    transition={{ delay: index * 0.12, duration: 0.5 }}
  >
    <div
      className="glass-card glass-card-hover rounded-xl h-full"
      style={{ borderTop: `2px solid ${pkg.color}` }}
    >
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="font-display text-2xl font-bold text-[#F0F4FF] flex items-center gap-2">
              <Package size={20} style={{ color: pkg.color }} />
              {pkg.name}
            </h3>
            <p className="font-mono text-sm" style={{ color: pkg.color }}>
              {pkg.tagline}
            </p>
          </div>
          <span
            className="px-2 py-0.5 rounded text-xs font-mono border flex-shrink-0 ml-3"
            style={{
              color: pkg.color,
              borderColor: `${pkg.color}40`,
              background: `${pkg.color}10`,
            }}
          >
            LIVE ON PYPI
          </span>
        </div>

        {/* Description */}
        <p className="text-sm text-[#C0C8D8] mb-4 leading-relaxed">
          {pkg.description}
        </p>

        {/* Install command */}
        <div
          className="flex items-center gap-2 px-3 py-2 rounded-lg font-mono text-xs mb-4"
          style={{
            color: pkg.color,
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${pkg.color}25`,
          }}
        >
          <Terminal size={13} className="flex-shrink-0" />
          <span className="text-[#8892A4]">$</span> {pkg.install}
        </div>

        {/* Links */}
        <div className="flex flex-wrap items-center gap-4">
          <a
            href={pkg.pypiUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-mono transition-opacity duration-200 hover:opacity-80"
            style={{ color: pkg.color }}
          >
            <Package size={14} /> View on PyPI <ExternalLink size={12} />
          </a>
          <a
            href={pkg.githubUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-mono transition-opacity duration-200 hover:opacity-80"
            style={{ color: pkg.color }}
          >
            <Github size={14} /> View on GitHub <ExternalLink size={12} />
          </a>
        </div>
      </div>
    </div>
  </motion.div>
);

const PublishedPackagesSection = () => {
  return (
    <section id="packages" className="py-24 relative z-10">
      <div className="container mx-auto px-6 lg:px-16">
        <motion.span
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="section-label block mb-4"
        >
          // LIVE ON PYPI
        </motion.span>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="font-display text-3xl lg:text-4xl font-bold text-[#F0F4FF] mb-3"
        >
          Python Packages
        </motion.h2>
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="font-mono text-sm text-[#8892A4] mb-12"
        >
          Open-source tooling for production LLM pipelines. One command to install.
        </motion.p>

        <div className="grid md:grid-cols-2 gap-6">
          {publishedPackages.map((pkg, index) => (
            <PackageCard key={index} pkg={pkg} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default PublishedPackagesSection;
